"use client";

import * as React from "react";
import { cn } from "../../lib/utils";

export interface SegmentedControlOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
}

export function SegmentedControl({
  options,
  value,
  onValueChange,
  ariaLabel,
  className,
}: SegmentedControlProps) {
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const moveSelection = React.useCallback(
    (direction: 1 | -1) => {
      if (options.length === 0) return;

      const currentIndex = Math.max(
        options.findIndex((option) => option.value === value),
        0,
      );

      let nextIndex = currentIndex;
      for (let step = 0; step < options.length; step += 1) {
        nextIndex = (nextIndex + direction + options.length) % options.length;
        if (!options[nextIndex]?.disabled) {
          const nextValue = options[nextIndex]?.value;
          if (nextValue) {
            onValueChange(nextValue);
            itemRefs.current[nextIndex]?.focus();
          }
          return;
        }
      }
    },
    [onValueChange, options, value],
  );

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex w-full rounded-lg border border-black bg-white p-1 shadow-[0_2px_0_0_#000000]",
        className,
      )}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            ref={(node) => {
              itemRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={option.disabled || undefined}
            disabled={option.disabled}
            tabIndex={isSelected ? 0 : -1}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors outline-none",
              isSelected
                ? "bg-primary text-white"
                : "text-[#667085] hover:text-[#101828]",
              option.disabled && "cursor-not-allowed opacity-50",
            )}
            onClick={() => onValueChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                moveSelection(1);
              }

              if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                moveSelection(-1);
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
